import { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack
} from '@mui/material';

import { FileDuplicateResolution, FileDuplicateSummary } from '../../../types/syntiro_honey';
import GhostButton from '../../shared/button/GhostButton';
import PrimaryButton from '../../shared/button/PrimaryButton';
import ConfirmationModal from '../../shared/modal/ConfirmationModal';
import { BodyText, CaptionText, Subheading } from '../../shared/typography/PrimaryTypography';

/** The dialog's choices: the three record-level resolutions plus the file delete. */
type DuplicateChoice = FileDuplicateResolution | 'DELETE_FILE';

/** One radio option with its consequence spelled out underneath. */
const CHOICES: Array<{ value: DuplicateChoice; label: string; caption: string }> = [
  {
    value: 'UPSERT',
    label: 'Process new and updated records (recommended)',
    caption:
      'Changed values replace the earlier ones — the approval stage shows the value ' +
      'changing from old to new. Identical duplicates are removed; genuinely new records stay.'
  },
  {
    value: 'ONLY_NEW',
    label: 'Only process new records',
    caption:
      'Every record that already exists on Syntiro is removed from this file, ' +
      'including changed values (value updates are ignored).'
  },
  {
    value: 'PROCESS_AS_NEW',
    label: 'Process everything as new records',
    caption:
      'Keeps all records, duplicates included — for genuinely repeated datapoints, ' +
      'like two identical diesel purchases in the same month.'
  },
  {
    value: 'DELETE_FILE',
    label: 'Delete this file and all its records',
    caption: 'Removes the whole upload, approved records included. An admin can restore it.'
  }
];

interface FileDuplicateChoiceDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The scoped file's display name. */
  fileName: string;
  /** The file's collision counts, for the summary line. */
  summary: FileDuplicateSummary;
  /** Whether a resolution/delete request is in flight. */
  busy: boolean;
  /** Apply one of the three record-level resolutions. */
  onResolve: (resolution: FileDuplicateResolution) => Promise<void>;
  /** Delete the file and all its records (the fourth choice). */
  onDeleteFile: () => Promise<void>;
  /** Close without deciding. */
  onClose: () => void;
}

/**
 * The 4-way duplicate decision for one processed file (Maxine 2026-08-19
 * scenario 2): upsert / only-new / everything-as-new / delete the file. The
 * destructive-by-surprise choices chain an explicit confirmation — "everything as
 * new" uses Maxine's own wording, and the file delete states its blast radius.
 * @param {boolean} open Whether the dialog is open.
 * @param {string} fileName The scoped file's display name.
 * @param {FileDuplicateSummary} summary Collision counts for the summary line.
 * @param {boolean} busy Whether a request is in flight.
 * @param {(resolution: FileDuplicateResolution) => Promise<void>} onResolve Resolution handler.
 * @param {() => Promise<void>} onDeleteFile File-delete handler.
 * @param {() => void} onClose Close handler.
 * @returns {JSX.Element}
 */
export default function FileDuplicateChoiceDialog({
  open,
  fileName,
  summary,
  busy,
  onResolve,
  onDeleteFile,
  onClose
}: FileDuplicateChoiceDialogProps) {
  const [choice, setChoice] = useState<DuplicateChoice>('UPSERT');
  // Which chained confirmation is showing, if any: Maxine's "are you sure" for
  // process-as-new, or the blast-radius confirm for the file delete.
  const [confirming, setConfirming] = useState<'PROCESS_AS_NEW' | 'DELETE_FILE' | null>(null);

  const total = summary.duplicateCount + summary.conflictCount;

  const applyChoice = (): void => {
    if (choice === 'PROCESS_AS_NEW' || choice === 'DELETE_FILE') {
      setConfirming(choice);
      return;
    }
    void onResolve(choice);
  };

  const handleConfirmed = (): void => {
    if (confirming === 'DELETE_FILE') {
      void onDeleteFile();
    } else if (confirming === 'PROCESS_AS_NEW') {
      void onResolve('PROCESS_AS_NEW');
    }
    setConfirming(null);
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <Subheading weight="Bold" sx={{ m: 2 }}>
        Records in this file already exist
      </Subheading>
      <DialogContent>
        <Stack spacing={2}>
          <BodyText>
            {`${total} record${total === 1 ? '' : 's'} in "${fileName}" already exist on Syntiro — ` +
              `${summary.duplicateCount} with the same value, ${summary.conflictCount} with a different value. ` +
              'Choose how to handle them. This decision is made once for the whole file.'}
          </BodyText>
          <FormControl>
            <RadioGroup
              value={choice}
              onChange={(event) => setChoice(event.target.value as DuplicateChoice)}
            >
              {CHOICES.map((option) => (
                <Stack key={option.value} sx={{ mb: 1 }}>
                  <FormControlLabel
                    value={option.value}
                    control={<Radio size="small" />}
                    label={option.label}
                    disabled={busy}
                  />
                  <CaptionText sx={{ ml: 4, color: 'text.secondary' }}>
                    {option.caption}
                  </CaptionText>
                </Stack>
              ))}
            </RadioGroup>
          </FormControl>
          <DialogActions sx={{ px: 0 }}>
            <GhostButton dark onClick={onClose} disabled={busy}>
              Decide later
            </GhostButton>
            <PrimaryButton onClick={applyChoice} loading={busy}>
              Apply
            </PrimaryButton>
          </DialogActions>
        </Stack>
      </DialogContent>
      <ConfirmationModal
        open={confirming === 'PROCESS_AS_NEW'}
        title="Add these records as new?"
        description={`Are you sure you want to add these records which were flagged as duplicates? All ${total} flagged record${total === 1 ? '' : 's'} in "${fileName}" will stay, next to the earlier values.`}
        confirm_button_text="Add as new records"
        confirm_loading={busy}
        handleCloseModal={() => setConfirming(null)}
        handleConfirm={handleConfirmed}
      />
      <ConfirmationModal
        open={confirming === 'DELETE_FILE'}
        title="Delete this file and its records?"
        description={`This deletes "${fileName}" and ALL records extracted from it, including any already approved. An admin can restore it later.`}
        confirm_button_text="Delete file and records"
        confirm_loading={busy}
        handleCloseModal={() => setConfirming(null)}
        handleConfirm={handleConfirmed}
      />
    </Dialog>
  );
}
